import { motion } from "framer-motion";
import {
  Truck, Clock, MessageSquareWarning, Copy, EyeOff, Boxes, DollarSign, TrendingDown,
  ShieldAlert, UserX, FileWarning, BookX, GaugeCircle, AlertTriangle,
} from "lucide-react";

const PROBLEMS = [
  { icon: Truck, t: "Delayed Customer Deliveries", d: "Late shipments hurt customer trust and revenue." },
  { icon: Clock, t: "Process Bottlenecks", d: "Hidden delays slow your entire operation." },
  { icon: MessageSquareWarning, t: "Communication Breakdowns", d: "Teams misaligned across silos and shifts." },
  { icon: Copy, t: "Duplicate Activities", d: "Redundant work wastes hours every week." },
  { icon: EyeOff, t: "Poor Process Visibility", d: "You can't improve what you can't see." },
  { icon: Boxes, t: "Inventory Inaccuracies", d: "Stock-outs, over-stocks, and write-offs." },
  { icon: DollarSign, t: "Excessive Costs", d: "Margins squeezed by hidden inefficiencies." },
  { icon: TrendingDown, t: "Low Productivity", d: "Talented people stuck in broken workflows." },
  { icon: ShieldAlert, t: "Compliance Risks", d: "Audit findings and regulatory exposure." },
  { icon: UserX, t: "Customer Dissatisfaction", d: "Churn rising. NPS sliding. Reviews suffering." },
  { icon: FileWarning, t: "Contractual Risks", d: "Hidden liabilities buried in fine print." },
  { icon: BookX, t: "Lack of Standardized Procedures", d: "Every shift does it differently." },
  { icon: GaugeCircle, t: "Missing Performance Metrics", d: "Decisions made on gut instead of data." },
];

export default function Challenges() {
  return (
    <section className="bg-[#F8FAFC] border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-7">
            <div className="section-eyebrow mb-5"><AlertTriangle size={12} /> The Symptoms</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
              Are these challenges holding your business back?
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-slate-600 leading-relaxed text-base">
              If you&apos;re nodding to any of these, you&apos;re not alone. We help organizations diagnose the
              root cause — and transform messy operations into measurable, scalable results.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {PROBLEMS.map((p, i) => (
            <motion.div
              key={p.t}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
              className="group relative bg-white border-l-2 border-[#EF4444] border-y border-r border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 grid place-items-center bg-[#EF4444]/10 text-[#EF4444] flex-shrink-0">
                  <p.icon size={18} />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[#0A2540] text-base mb-1">{p.t}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{p.d}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
